import { ApiError } from '@mevaser/shared'
import { ToastProvider } from '@mevaser/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './app.css'
import { setUnauthorizedHandler } from './lib/api.js'
import { AuthProvider } from './lib/auth.js'
import { registerServiceWorker } from './lib/push.js'
import { ThemeProvider } from './lib/theme.js'
import { router } from './router.js'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retrying a 403 or a 404 wastes the user's time and hides the real
      // answer; only transient failures are worth a second attempt.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.isTransient && failureCount < 2,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
})

/**
 * The signed-out screens. A 401 on any of them is expected — nobody is signed
 * in yet — and redirecting would be actively harmful on /reset-password,
 * where it would discard the token from the emailed link.
 */
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password']

// One place handles an expired session, rather than every screen guessing.
setUnauthorizedHandler(() => {
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    queryClient.clear()
    window.location.href = '/login'
  }
})

registerServiceWorker()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element is missing')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
